import { useState, useCallback } from 'react';
import { useOnceEffect } from './useOnceEffect';
import { 
    FinancialInstitution
} from '@/types/financialInstitution';
import {
    getFinancialInstitution as fetchInstitution,
    createFinancialInstitution as createInstitution,
    updateFinancialInstitution as updateInstitution,
    uploadInstitutionLogo as uploadLogo,
    CreateFinancialInstitution,
} from '@/services/financialInstitution'; // Pour les mocks
import financialInstitutionApiService from '@/services/financialInstitutionApi'; // Pour l'API réelle
import { useToast } from '@/hooks/useToast';

/**
 * ✨ PAS DE CONVERSION NÉCESSAIRE !
 * FinancialInstitutionResponse = CreateFinancialInstitutionRequest + id/dates
 * Le backend renvoie TOUTES les données du formulaire sans perte
 * On peut utiliser les données directement
 */

interface UseFinancialInstitutionOptions {
  enabled?: boolean;
  preferMock?: boolean;
}

export const useFinancialInstitution = (
    institutionId?: string, 
    options: UseFinancialInstitutionOptions = { enabled: true, preferMock: false }
) => {
    const [institution, setInstitution] = useState<FinancialInstitution | null>(null);
    const [loading, setLoading] = useState<boolean>(options.enabled ?? true);
    const [error, setError] = useState<string | null>(null);
    const [isUpdating, setIsUpdating] = useState<boolean>(false);
    const [usingMockData, setUsingMockData] = useState<boolean>(options.preferMock || false);
    
    const { showToast } = useToast();

    const fetchInstitutionData = useCallback(async () => {
        if (!institutionId || !options.enabled) {
            setLoading(false);
            return;
        }
        
        try {
            setLoading(true);
            
            let institutionData: FinancialInstitution | null = null;
            
            if (!options.preferMock) {
                try {
                    // Essayer d'abord d'obtenir les données depuis l'API
                    console.log(`🌐 Tentative de récupération de l'institution depuis l'API: ${institutionId}`);
                    const apiData = await financialInstitutionApiService.getInstitution(institutionId);
                    institutionData = apiData as any; // ✨ Données directes, pas de conversion
                    setUsingMockData(false);
                    console.log('✅ Données récupérées depuis l\'API avec succès');
                } catch (apiError) {
                    console.warn(`⚠️ Erreur API, utilisation des données mockées: ${apiError instanceof Error ? apiError.message : 'Erreur inconnue'}`);
                    // En cas d'échec de l'API, fallback sur les données mockées
                    institutionData = await fetchInstitution(institutionId);
                    setUsingMockData(true);
                }
            } else {
                // Utilisation directe des données mockées si préférées
                console.log(`🧪 Utilisation préférée des données mockées pour l'institution: ${institutionId}`);
                institutionData = await fetchInstitution(institutionId);
                setUsingMockData(true);
            }
            
            setInstitution(institutionData);
            setError(null);
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Une erreur est survenue.';
            setError(errorMessage);
            console.error('❌ Erreur lors de la récupération de l\'institution:', errorMessage);
            showToast({ type: 'error', title: 'Erreur', message: `Erreur lors de la récupération de l'institution : ${errorMessage}` });
        } finally {
            setLoading(false);
        }
    }, [institutionId, options.enabled, options.preferMock, showToast]);

    // Charger les données une seule fois pour éviter les boucles
    useOnceEffect(() => {
        fetchInstitutionData();
    });

    const createNewInstitution = async (institutionData: CreateFinancialInstitution) => {
        try {
            setIsUpdating(true);
            
            let newInstitution: FinancialInstitution;
            
            try {
                if (options.preferMock) {
                    throw new Error('Utilisant des données mockées par préférence');
                }
                
                // Essayer d'abord l'API
                console.log('📤 Création d\'une institution via l\'API...');
                const apiData = await financialInstitutionApiService.createInstitution(institutionData as any);
                newInstitution = apiData as any; // ✨ Données directes, pas de conversion
                console.log('✅ Institution créée via l\'API avec succès');
                
                // Créer également dans le mock pour garder la cohérence
                await createInstitution(institutionData);
                setUsingMockData(false);
            } catch (apiError) {
                console.warn(`⚠️ Impossible de créer via l'API, utilisation du mock: ${apiError instanceof Error ? apiError.message : 'Erreur inconnue'}`);
                // En cas d'échec, création via le mock
                newInstitution = await createInstitution(institutionData);
                setUsingMockData(true);
            }
            
            setInstitution(newInstitution);
            showToast({ type: 'success', title: 'Succès', message: 'Institution financière créée avec succès !' });
            
            return newInstitution;
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Une erreur est survenue.';
            setError(errorMessage);
            console.error('❌ Erreur lors de la création de l\'institution:', errorMessage);
            showToast({ type: 'error', title: 'Erreur', message: `Erreur lors de la création de l'institution : ${errorMessage}` });
            return null;
        } finally {
            setIsUpdating(false);
        }
    };

    const updateInstitutionData = async (updates: Partial<FinancialInstitution>) => {
        if (!institutionId) return null;

        try {
            setIsUpdating(true);
            
            let updatedInstitution: FinancialInstitution;
            
            try {
                if (usingMockData) {
                    throw new Error('Utilisant des données mockées, continuant avec le mock');
                }
                
                // Essayer d'abord l'API
                console.log('📤 Envoi des données à l\'API...');
                const apiCompatibleData = {
                  ...updates, // ✨ Données directes, pas de conversion
                  id: institutionId
                };
                const apiData = await financialInstitutionApiService.updateInstitution(institutionId, apiCompatibleData);
                updatedInstitution = apiData as any; // ✨ Données directes, pas de conversion
                console.log('✅ Données envoyées à l\'API avec succès');
                
                // Mettre à jour également le mock pour garder la cohérence
                await updateInstitution(institutionId, updates);
            } catch (apiError) {
                console.warn(`⚠️ Impossible de mettre à jour via l'API, utilisation du mock: ${apiError instanceof Error ? apiError.message : 'Erreur inconnue'}`);
                // En cas d'échec, mise à jour via le mock
                updatedInstitution = await updateInstitution(institutionId, updates);
                setUsingMockData(true);
            }
            
            setInstitution(updatedInstitution);
            showToast({ type: 'success', title: 'Succès', message: 'Informations mises à jour avec succès !' });
            
            return updatedInstitution;
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Une erreur est survenue.';
            setError(errorMessage);
            console.error('❌ Erreur lors de la mise à jour de l\'institution:', errorMessage);
            showToast({ type: 'error', title: 'Erreur', message: `Erreur lors de la mise à jour : ${errorMessage}` });
            return null;
        } finally {
            setIsUpdating(false);
        }
    };

    const uploadLogoFile = async (logoFile: File) => {
        if (!institutionId) return null;

        try {
            setIsUpdating(true);
            
            let response: { url: string };
            
            try {
                if (usingMockData) {
                    throw new Error('Utilisant des données mockées, continuant avec le mock');
                }
                
                // Essayer d'abord l'API
                console.log('📤 Upload du logo via l\'API...');
                response = await financialInstitutionApiService.uploadLogo(institutionId, logoFile);
                console.log('✅ Logo uploadé via l\'API avec succès');
                
                // Mettre à jour également le mock pour garder la cohérence
                await uploadLogo(institutionId, logoFile);
            } catch (apiError) {
                console.warn(`⚠️ Impossible d'uploader via l'API, utilisation du mock: ${apiError instanceof Error ? apiError.message : 'Erreur inconnue'}`);
                // En cas d'échec, upload via le mock
                response = await uploadLogo(institutionId, logoFile);
                setUsingMockData(true);
            }
            
            // Mise à jour du state avec le nouvel URL du logo
            if (institution) {
                // TODO: Une fois que nous ajouterons logo au formulaire, mettre à jour ici
                // const updatedInstitution = { ...institution, logo: response.url };
                // setInstitution(updatedInstitution);
                
                // TODO: Gérer la mise à jour du logo dans la nouvelle structure
                // Pour l'instant, pas de mise à jour automatique car le logo n'est plus dans FinancialInstitution
                console.log('Logo uploadé mais pas encore intégré dans la structure de données');
            }
            
            showToast({ type: 'success', title: 'Succès', message: 'Logo uploadé avec succès !' });
            return response.url;
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Une erreur est survenue.';
            setError(errorMessage);
            console.error('❌ Erreur lors de l\'upload du logo:', errorMessage);
            showToast({ type: 'error', title: 'Erreur', message: `Erreur lors de l'upload du logo : ${errorMessage}` });
            return null;
        } finally {
            setIsUpdating(false);
        }
    };


    // Fonction pour forcer le rafraîchissement des données
    const refreshInstitution = () => {
        fetchInstitutionData();
    };
    
    return {
        institution,
        loading,
        isUpdating,
        error,
        usingMockData, // Nouvel indicateur pour savoir si on utilise les données mockées
        fetchInstitutionData,
        refreshInstitution,
        createNewInstitution,
        updateInstitutionData,
        uploadLogoFile,
    };
};
